// React
import { memo, useCallback } from "react";

// Helpers
import { classNames } from "helpers";

// Tipovi
import { BillingPeriod, BillingPeriodPickerProps } from "./types";

/**
 * Odabir načina plaćanja karticama.
 *
 * Popust na godišnje plaćanje prije je stajao kao sitna rečenica ispod dvaju kružića, pa se
 * lako previdio. Sada stoji na samoj kartici koja ga nosi, gdje se i odlučuje.
 */
const BillingPeriodPicker: React.FC<BillingPeriodPickerProps> = (
  props: BillingPeriodPickerProps
) => {
  const { value, annualDiscountPercent, onChange } = props;

  const handleSelect = useCallback(
    (period: BillingPeriod) => () => {
      onChange(period);
    },
    [onChange]
  );

  const cardClassName = (isSelected: boolean): string =>
    classNames(
      "text-left border px-4 py-3 transition-colors",
      isSelected
        ? "border-sky-600 bg-sky-50 ring-1 ring-sky-600"
        : "border-slate-200 bg-white hover:border-slate-300"
    );

  const titleClassName = (isSelected: boolean): string =>
    classNames("text-sm font-semibold", isSelected ? "text-sky-900" : "text-slate-700");

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <button
        type="button"
        onClick={handleSelect("Monthly")}
        aria-pressed={value === "Monthly"}
        className={cardClassName(value === "Monthly")}
      >
        <p className={titleClassName(value === "Monthly")}>Mjesečno</p>

        <p className="text-xs text-slate-500 mt-1">Račun stiže svaki mjesec.</p>
      </button>

      <button
        type="button"
        onClick={handleSelect("Annual")}
        aria-pressed={value === "Annual"}
        className={cardClassName(value === "Annual")}
      >
        <div className="flex items-start justify-between gap-3">
          <p className={titleClassName(value === "Annual")}>Godišnje</p>

          {annualDiscountPercent !== null && (
            <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">
              −{annualDiscountPercent.toFixed(1).replace(".", ",")} %
            </span>
          )}
        </div>

        <p className="text-xs text-slate-500 mt-1">
          Plaća se unaprijed za godinu dana{annualDiscountPercent !== null && ", uz popust"}.
        </p>
      </button>
    </div>
  );
};

export default memo(BillingPeriodPicker);
